import { useEffect, useRef, useState } from 'react';

interface TradingViewAdvancedChartProps {
  symbol: string;
  defaultTimeframe?: string;
  height?: number;
}

// Per-symbol overrides for indices that each live on a different TV exchange namespace
export const SYMBOL_TV_OVERRIDE: Record<string, string> = {
  // Indices (from atlas-incident-11)
  'GSPC.INDX': 'SP:SPX',
  'IXIC.INDX': 'NASDAQ:IXIC',
  'DJI.INDX': 'DJ:DJI',
  // NYSE Arca ETFs — TradingView uses AMEX prefix for NYSE Arca
  'GLD.US':  'AMEX:GLD',
  'SLV.US':  'AMEX:SLV',
  'PPLT.US': 'AMEX:PPLT',
  'USO.US':  'AMEX:USO',
  'UNG.US':  'AMEX:UNG',
};

// Map our exchange codes to TradingView exchange prefixes
const EXCHANGE_TV_MAP: Record<string, string> = {
  JSE: 'JSE',
  US: 'NASDAQ',
  LSE: 'LSE',
  TO: 'TSX',
  XETRA: 'XETRA',
  AU: 'ASX',
  FOREX: 'OANDA',
  HK: 'HKEX',
};

// Map our timeframe labels to TradingView interval strings
const TIMEFRAME_TV_MAP: Record<string, string> = {
  '5M': '5',
  '15M': '15',
  '30M': '30',
  '1H': '60',
  '1D': 'D',
  '1W': 'W',
  '1M': 'D',
  '3M': 'D',
  '1Y': 'D',
};

export function toTvSymbol(symbol: string): string {
  if (symbol in SYMBOL_TV_OVERRIDE) return SYMBOL_TV_OVERRIDE[symbol];

  const dot = symbol.lastIndexOf('.');
  if (dot === -1) return `NASDAQ:${symbol}`;
  const code = symbol.slice(0, dot);
  const exchange = symbol.slice(dot + 1);

  // Crypto tickers carry a hyphen (BTC-USD) that TradingView's Coinbase
  // feed does not accept — strip it before constructing the symbol.
  if (exchange === 'CC') {
    return `COINBASE:${code.replace(/-/g, '')}`;
  }

  return `${EXCHANGE_TV_MAP[exchange] ?? exchange}:${code}`;
}

// Load tv.js once per page session — subsequent calls return the same promise
let tvScriptPromise: Promise<void> | null = null;
function loadTvScript(): Promise<void> {
  if ((window as any).TradingView) return Promise.resolve();
  if (!tvScriptPromise) {
    tvScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        tvScriptPromise = null; // allow retry on next mount
        reject(new Error('Failed to load TradingView script'));
      };
      document.head.appendChild(script);
    });
  }
  return tvScriptPromise;
}

export default function TradingViewAdvancedChart({
  symbol,
  defaultTimeframe = '1D',
  height = 420,
}: TradingViewAdvancedChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  const tvSymbol = symbol ? toTvSymbol(symbol) : null;
  const interval = TIMEFRAME_TV_MAP[defaultTimeframe] ?? 'D';
  const containerId = tvSymbol
    ? `tv-chart-${tvSymbol.replace(':', '-').replace(/[^a-zA-Z0-9-]/g, '')}`
    : 'tv-chart-placeholder';

  useEffect(() => {
    if (!tvSymbol) return; // empty / invalid symbol — render nothing

    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    setLoading(true);

    loadTvScript()
      .then(() => {
        if (cancelled || !container || !(window as any).TradingView) return;
        // Clear any widget rendered for the previous symbol
        container.innerHTML = '';
        new (window as any).TradingView.widget({
          container_id: containerId,
          symbol: tvSymbol,
          interval,
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1', // candlestick
          locale: 'en',
          toolbar_bg: '#0f172a',
          hide_side_toolbar: false,
          allow_symbol_change: false,
          width: '100%',
          height,
          studies: ['RSI@tv-basicstudies', 'MACD@tv-basicstudies'],
          disabled_features: ['header_symbol_search', 'header_compare'],
          enabled_features: ['use_localstorage_for_settings'],
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // Clear widget DOM on unmount or before next symbol renders
      if (container) container.innerHTML = '';
    };
  }, [tvSymbol, interval, height, containerId]);

  if (!tvSymbol) return null;

  return (
    <div className="relative rounded-lg overflow-hidden" style={{ height, width: '100%' }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <div className="w-8 h-8 border-2 border-slate-600 border-t-teal-400 rounded-full animate-spin" />
        </div>
      )}
      <div id={containerId} ref={containerRef} style={{ height, width: '100%' }} />
    </div>
  );
}
